import type { PDFFont } from 'pdf-lib';
/** `baseline` is the first line's baseline, measured up from the bottom of the box. Text is placed by its block: from the
 * top of the first line's capitals to the bottom of the last line's descenders. Set at the bottom, the descenders reach
 * the bottom edge; at the top, the capitals reach the top edge; in the middle, the block is centered. Keeping the baseline
 * comes first: in a box too short for its text, the last line's descenders sit on the bottom edge and the text rises
 * above the top. */
/** A font's measures, in ems: how far its capitals rise above the baseline, and how far its descenders drop below. */
export interface PdfFontMetrics { cap: number; descent: number }
/** The handwriting font's measures. Most answers are written in it. */
export const PDF_HAND_METRICS: PdfFontMetrics = { cap: .72, descent: .43 };
/** The cursive signature font's measures. */
export const PDF_SIGNATURE_METRICS: PdfFontMetrics = { cap: 775 / 1024, descent: 663 / 1024 };
/** An embedded font's measures, read from the font itself. */
export function pdfFontMetrics(font: PDFFont): PdfFontMetrics {
  const own = (font as unknown as { embedder?: { font?: { capHeight?: number; descent?: number; unitsPerEm?: number; CapHeight?: number; Descender?: number } } }).embedder?.font;
  if (own?.unitsPerEm && own.capHeight !== undefined && own.descent !== undefined) return { cap: own.capHeight / own.unitsPerEm, descent: -own.descent / own.unitsPerEm };
  if (own?.CapHeight !== undefined && own.Descender !== undefined) return { cap: own.CapHeight / 1000, descent: -own.Descender / 1000 };
  const ascent = font.heightAtSize(1, { descender: false });
  return { cap: ascent, descent: font.heightAtSize(1) - ascent };
}
/** Where text sits up and down in a field: unset, one line sits on the bottom edge and several start at the top. */
export type PdfVerticalAlignment = 'top' | 'middle' | 'bottom';
export const pdfVerticalAlignment = (p: { verticalAlignment?: PdfVerticalAlignment; multiline: boolean }): PdfVerticalAlignment => p.verticalAlignment ?? (p.multiline ? 'top' : 'bottom');
/** The first line's baseline, up from the bottom of a box `height` tall, for text set at the top, middle or bottom.
 * Keeping the baseline comes first: a block too tall for the box rests its last descenders on the bottom edge and rises. */
export function pdfFirstBaseline(layout: Pick<PdfTextLayout, 'lines' | 'cap' | 'descent' | 'lineHeight'>, height: number, vertical: PdfVerticalAlignment): number {
  const lowest = layout.descent + Math.max(0, layout.lines.length - 1) * layout.lineHeight, block = layout.cap + lowest;
  const at = vertical === 'top' ? height - layout.cap : vertical === 'bottom' ? lowest : (height + block) / 2 - layout.cap;
  return Math.max(lowest, at);
}
/** Answer text sits this far inside a field's top and bottom edges. */
export const PDF_TEXT_INSET = 1;
/** The stroke weight of a checkmark drawn in a box `size` across. */
export const pdfCheckWeight = (size: number) => Math.max(1.25, size / 7);
/** The checkmark in a checkbox field `width` by `height` points: a short stroke down to the bottom point and a long one
 * up from it, both `weight` thick with round ends, centered in the box 2 points inside its edges. Points are measured
 * from the box's bottom left, up and to the right. The PDF and the editor both draw it from here, so they match. */
export function pdfCheckmark(width: number, height: number): { points: [[number, number], [number, number], [number, number]]; weight: number } {
  const size = Math.min(width - 4, height - 4), x = (width - size) / 2, y = (height - size) / 2;
  return { points: [[x + size * .15, y + size * .5], [x + size * .42, y + size * .2], [x + size * .85, y + size * .85]], weight: pdfCheckWeight(size) };
}
/** `cap` and `descent` are the capitals' height and descenders' depth at the text's size, in points; `height` the block's. */
export interface PdfTextLayout { lines: string[]; fontSize: number; lineHeight: number; cap: number; descent: number; baseline: number; height: number }
/** A layout with the font it was set in, which writes it. */
export interface PdfFontLayout extends PdfTextLayout { font: PDFFont; measure: PdfTextMeasure }
const segments = new Intl.Segmenter('en', { granularity: 'grapheme' });
export type PdfTextMeasure = ((text: string, size: number) => number) & { additive?: boolean };
/** Cache exact shaped unit widths only for this generation operation. No global answer
 * cache: keys are participant text fragments and are released with the document. */
export function createPdfTextMeasure(font: PDFFont, additive = false): PdfTextMeasure {
  const widths = new Map<string, number>();
  const graphemes = new Map<string, number>();
  const measure: PdfTextMeasure = (text, size) => {
    let unit = widths.get(text);
    if (unit === undefined) {
      unit = 0;
      if (additive) for (const { segment } of segments.segment(text)) {
        let width = graphemes.get(segment);
        if (width === undefined) { width = font.widthOfTextAtSize(segment, 1); graphemes.set(segment, width); }
        unit += width;
      } else unit = font.widthOfTextAtSize(text, 1);
      if (widths.size < 32768) widths.set(text, unit);
    }
    return unit * size;
  };
  measure.additive = additive; return measure;
}

/** Preserve line breaks and spaces. Tabs expand to four spaces. Break long tokens at
 * grapheme boundaries; never clip or replace glyphs. Several lines are set at the preferred size; a single line too wide
 * shrinks, never below the supplied minimum. Height never shrinks text. */
export function layoutPdfText(text: string, font: PDFFont, width: number, height: number, preferred: number, minimum: number, multiline: boolean, measure: PdfTextMeasure = createPdfTextMeasure(font)): PdfTextLayout | undefined {
  text = text.replace(/\r\n?/g, '\n').replace(/\t/g, '    ');
  if (![width, height, preferred, minimum].every(Number.isFinite) || width <= 0 || height <= 0 || minimum < 7 || preferred < minimum) return undefined;
  if (!multiline && text.includes('\n')) return undefined;
  const wrap = (size: number): string[] | undefined => {
    if (!multiline) return measure(text, size) <= width ? [text] : undefined;
    const lines: string[] = [];
    for (const paragraph of text.split('\n')) {
      let line = '', lineWidth = 0;
      // Preserve whitespace while preferring a wrap before an entire word.
      for (const token of paragraph.match(/\s+|\S+/gu) ?? []) {
        const tokenWidth = measure(token, size), nextWidth = measure.additive ? lineWidth + tokenWidth : measure(line + token, size);
        if (nextWidth <= width) { line += token; lineWidth = nextWidth; continue; }
        if (line) { lines.push(line); line = ''; lineWidth = 0; }
        for (const { segment } of segments.segment(token)) {
          const segmentWidth = measure(segment, size); if (segmentWidth > width) return undefined;
          let nextWidth = measure.additive ? lineWidth + segmentWidth : measure(line + segment, size);
          if (line && nextWidth > width) { lines.push(line); line = ''; lineWidth = 0; nextWidth = segmentWidth; }
          line += segment; lineWidth = nextWidth;
        }
      }
      lines.push(line);
    }
    return lines;
  };
  const metrics = pdfFontMetrics(font);
  const atSize = (size: number): PdfTextLayout | undefined => {
    const lines = wrap(size), cap = metrics.cap * size, descent = metrics.descent * size;
    const lineHeight = font.heightAtSize(size) * 1.15;
    const total = lines ? cap + Math.max(0, lines.length - 1) * lineHeight + descent : Infinity;
    // A field's height never shrinks its text: one line may rise above a short box.
    if (lines && (!multiline || total <= height)) return { lines, fontSize: size, lineHeight, cap, descent, baseline: multiline ? height - cap : descent, height: total };
  };
  const full = atSize(preferred); if (full || multiline) return full;
  // Only a single line too wide for its field shrinks, down to the minimum size.
  let best = atSize(minimum); if (!best) return undefined;
  // Fit is monotonic with decreasing size. Bound expensive shaping to a logarithmic
  // search rather than traversing every half-point from 24 down to seven.
  let low = 0, high = Math.ceil((preferred - minimum) * 2);
  while (high - low > 1) {
    const mid = Math.floor((low + high) / 2), candidate = atSize(minimum + mid / 2);
    if (candidate) { best = candidate; low = mid; } else high = mid;
  }
  return best;
}

/** An answer too long for its field: as much of it as fits at `size`, starting from the beginning. Lines that don't fit are left off, and a line too wide is cut at a character boundary. A field shows
 * what fits; it never stops the form. */
export function clipPdfText(text: string, font: PDFFont, width: number, height: number, size: number, multiline: boolean, measure: PdfTextMeasure = createPdfTextMeasure(font)): PdfTextLayout {
  const metrics = pdfFontMetrics(font), cap = metrics.cap * size, descent = metrics.descent * size;
  const lineHeight = font.heightAtSize(size) * 1.15;
  text = text.replace(/\r\n?/g, '\n').replace(/\t/g, '    ');
  // The longest start of a line that fits the width, by whole characters.
  const cut = (line: string) => { let kept = ''; for (const { segment } of segments.segment(line)) { if (measure(kept + segment, size) > width) break; kept += segment; } return kept; };
  const lines: string[] = [];
  if (!multiline) lines.push(cut(text.replace(/\n/g, ' ')));
  else for (const paragraph of text.split('\n')) {
    let line = '';
    for (const token of paragraph.match(/\s+|\S+/gu) ?? []) {
      if (measure(line + token, size) <= width) { line += token; continue; }
      if (line) { lines.push(line); line = ''; }
      // A word wider than the field is broken across lines, character by character.
      for (const { segment } of segments.segment(token)) {
        if (measure(line + segment, size) <= width) { line += segment; continue; }
        if (line) lines.push(line);
        line = measure(segment, size) <= width ? segment : '';
      }
    }
    lines.push(line);
  }
  // At least one line always shows, its descenders on the bottom edge if the box is too short for it: text rises above
  // a short box rather than dropping below it.
  const fit = multiline ? 1 + Math.max(0, Math.floor((height - cap - descent) / lineHeight)) : 1;
  const shown = lines.slice(0, fit);
  return { lines: shown, fontSize: size, lineHeight, cap, descent, baseline: multiline ? Math.max(descent, height - cap) : descent, height: shown.length ? cap + (shown.length - 1) * lineHeight + descent : 0 };
}
